/* eslint-disable @typescript-eslint/no-unused-vars */

import { SubscriptionChangeBody } from 'lib/srp';
import {
  verifySession,
  wipeSession,
  SessionVerificationError,
} from 'lib/session';
import { getRequest, postRequest, putRequest } from 'lib/api-proxy-client';
import { Subscription } from 'entities/subscription';
import type { SurveySubmitResult } from 'entities/survey-types';
import { trackCustomError } from 'lib/datadog-rum';

// note that the browser is actually just making calls to relative paths
// as "ROOT_URL" is visible to the browser
const root = (process && process.env && process.env.ROOT_URL) || '';
const url = `${root}/api`;

const opt = (
  method: 'POST' | 'GET' | 'PUT' | 'DELETE',
  data?: any,
): RequestInit => {
  const query: RequestInit = {
    method,
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
  if (data) {
    query.body = JSON.stringify(data);
  }
  return query;
};

// AUTH

export async function apiValidateGoogleAuthCode(
  code,
  redirectUrl = process.env.NEXT_PUBLIC_SOCIAL_SIGNON_REDIRECT_URL,
) {
  const endpoint = '/account/google-signon/';
  const httpResponse = await postRequest(endpoint, {
    code,
    redirect_url: redirectUrl,
  });
  const jsonResult = await httpResponse.json();
  jsonResult.isNewUser = httpResponse.status === 201;
  const response = jsonResult;
  return response;
}

export async function apiValidateFacebookAuthCode(
  code,
  redirectUrl = process.env.NEXT_PUBLIC_SOCIAL_SIGNON_REDIRECT_URL,
) {
  const endpoint = '/account/facebook-signon/';
  const httpResponse = await postRequest(endpoint, {
    code,
    redirect_url: redirectUrl,
  });
  const jsonResult = await httpResponse.json();
  jsonResult.isNewUser = httpResponse.status === 201;
  const response = jsonResult;
  return response;
}

export async function apiRecover(data) {
  await postRequest('/account/recover/', data);
}

export async function apiLogin(data) {
  const response = await postRequest('/account/login/', data);
  const json = await response.json();
  return json;
}

export async function apiGetUserExperiment(experimentName: string) {
  const response = await putRequest(
    `/account/user-experiment/${experimentName}`,
    {},
    { throwOnError: false },
  );
  const json = await response.json();
  return json;
}

export async function apiResetPassword(data: {
  password: string;
  token: string;
}) {
  const response = await postRequest('/account/reset-password/', data);
  await response.json();
}

export async function apiVerifyEmail(token: string) {
  const response = await getRequest(`/account/verify-account/${token}`);
  const json = await response.json();
  return json;
}

export async function apiVerifyCancellationToken(token) {
  return await getRequest(`/api/external_agent/authorize/${token}/`);
}

export async function apiLogout() {
  const returnUrl = window.location.origin;
  const endpoint = `${url}/auth/logout?return_url=${encodeURIComponent(returnUrl)}`;
  wipeSession();
  const response = await fetch(endpoint, opt('GET'));
  const data = await response.json().catch(() => ({}));
  return { status: response.status, data };
}

export async function apiCheckAuthentication(): Promise<{
  success: boolean;
  loginMethod: string | null;
}> {
  const endpoint = `${url}/auth/check-authentication`;
  const response = await fetch(endpoint, opt('GET'));
  const responseJson = await response.json();
  return {
    success: responseJson.success,
    loginMethod: responseJson.loginMethod,
  };
}

// CMS
export async function apiCmsGet(data) {
  try {
    const endpoint = `${url}/cms`;

    const response = await fetch(endpoint, opt('POST', data));
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: err };
  }
}

// MAILTO
export async function apiSendUniversity(data) {
  try {
    const endpoint = `${url}/mail/university`;

    const response = await fetch(endpoint, opt('POST', data));
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: err };
  }
}
export async function apiSeedHCP(data) {
  try {
    const endpoint = `${url}/mail/hcp`;

    const response = await fetch(endpoint, opt('POST', data));
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: err };
  }
}

export async function apiSeedMagic(data) {
  try {
    const endpoint = `${url}/mail/magic`;
    const response = await fetch(endpoint, opt('POST', data));
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: err };
  }
}

export async function apiPurchasePreview(data) {
  try {
    const response = await postRequest(
      '/ecommerce/purchase/preview_v2/',
      data,
      { throwOnError: false },
    );
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: err };
  }
}

// SUBSCRIPTION
export async function apiSubscriptionsGet(includeRenewalPreview = false) {
  let endpoint = '/ecommerce/subscription/';
  if (includeRenewalPreview) {
    endpoint = `${endpoint}?include_renewal_preview=true`;
  }
  const response = await (await getRequest(endpoint)).json();
  return response;
}

export async function apiSubscriptionGetById(
  subscriptionId: number | string,
): Promise<Subscription> {
  const endpoint = `/ecommerce/subscription/${subscriptionId}`;
  const response = await (await getRequest(endpoint)).json();
  return response;
}

export async function apiSubscriptionPut(data) {
  try {
    const endpoint = `${url}/subscription`;

    const response = await fetch(endpoint, opt('PUT', data));
    const json = await response.json();
    if (response.status === 200 && typeof window !== 'undefined') {
      verifySession(json.seedSessionToken);
    }
    return { status: response.status, data: json };
  } catch (err) {
    if (err instanceof SessionVerificationError) {
      return {
        status: 401,
        data: {
          msg: 'SessionVerificationError',
          token: err.token,
          sessionToken: err.sessionToken,
        },
      };
    }
    return { status: 500, data: err };
  }
}

export async function apiShippingAddressPut(data) {
  try {
    const endpoint = `${url}/shipping-address`;

    const response = await fetch(endpoint, opt('PUT', data));
    const json = await response.json();
    if (response.status === 200 && typeof window !== 'undefined') {
      verifySession(json.seedSessionToken);
    }

    return { status: response.status, data: json.data };
  } catch (err) {
    if (err instanceof SessionVerificationError) {
      return {
        status: 401,
        data: {
          msg: 'SessionVerificationError',
          token: err.token,
          sessionToken: err.sessionToken,
        },
      };
    }
    return { status: 500, data: err };
  }
}

export async function apiGetCountries(addressType) {
  const response = await getRequest(
    `/region/options/${addressType}/countries/?format=json`,
  );
  const json = await response.json();
  return json;
}

export async function apiBillingDatePut(data) {
  try {
    const response = await putRequest(
      '/ecommerce/subscription/billing-date/',
      { subscription_id: data.id, update: data },
      { throwOnError: false },
    );
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: err };
  }
}

export async function apiReactivateSubscription(subscriptionId, purchaseBody) {
  try {
    const endpoint = `${url}/subscription/${subscriptionId}/reactivate`;

    const response = await fetch(endpoint, opt('PUT', purchaseBody));
    const json = await response.json();
    if (response.status === 200) {
      // we are not setting the seed jwt token into the response header in the MSL backend
      typeof window !== 'undefined' && verifySession(json.seedSessionToken);
    }
    return {
      status: response.status,
      data: json,
    };
  } catch (err) {
    if (err instanceof SessionVerificationError) {
      return {
        status: 401,
        data: {
          msg: 'SessionVerificationError',
          token: err.token,
          sessionToken: err.sessionToken,
        },
      };
    }
    return {
      status: 500,
      data: err,
    };
  }
}

/**
 * Previews the result of specific subscription changes
 * @param {string} subscriptionId Identifier of the subscription to be changed
 * @param {*} subscriptionChangeBody The change request body to be applied
 * @returns Subscription with requested changes applied
 */
export async function apiSubscriptionChangePreview(
  subscriptionId,
  subscriptionChangeBody,
): Promise<{ status: number; data: any }> {
  try {
    const endpoint = `${url}/subscription/${subscriptionId}/preview`;

    const response = await fetch(endpoint, opt('PUT', subscriptionChangeBody));
    const json = await response.json();
    typeof window !== 'undefined' && verifySession(json.seedSessionToken);
    return {
      status: response.status,
      data: json,
    };
  } catch (err) {
    if (err instanceof SessionVerificationError) {
      return {
        status: 401,
        data: {
          msg: 'SessionVerificationError',
          token: err.token,
          sessionToken: err.sessionToken,
        },
      };
    }
    return {
      status: 500,

      // FIXME: compare to previous version to see bug
      data: undefined, // was: 'err' probably should be 'error' (the exception) but propagating that variable would be new behaviour
    };
  }
}

/**
 * Creates a subscription change on the user's subscription
 * @param {string} subscriptionId Identifier of the subscription to be changed
 * @param {*} subscriptionChangeBody The change request body to be applied
 * @returns Subscription with requested changes applied
 */
export async function apiSubscriptionChange(
  subscriptionId: string | number,
  subscriptionChangeBody: SubscriptionChangeBody,
): Promise<{ status: number; data: any }> {
  try {
    const endpoint = `${url}/subscription/${subscriptionId}/change`;

    const response = await fetch(endpoint, opt('PUT', subscriptionChangeBody));
    const json = await response.json();
    if (response.status === 200 && typeof window !== 'undefined') {
      verifySession(json.seedSessionToken);
    }

    return {
      status: response.status,
      data: json.data,
    };
  } catch (err) {
    if (err instanceof SessionVerificationError) {
      return {
        status: 401,
        data: {
          msg: 'SessionVerificationError',
          token: err.token,
          sessionToken: err.sessionToken,
        },
      };
    }
    return {
      status: 500,
      // FIXME: compare to previous version to see bug
      data: undefined, // was: 'err' probably should be 'error' (the exception) but propagating that variable would be new behaviour
    };
  }
}

export async function apiSubscriptionDel(
  id,
  cancellationReasonId,
  secondaryReasonId,
  cancellationReasonText,
) {
  try {
    const endpoint = `${url}/subscription/${id}`;

    const response = await fetch(
      endpoint,
      opt('DELETE', {
        cancellationReasonId,
        cancellationReasonText,
        secondaryReasonId,
      }),
    );
    const json = await response.json();
    if (response.ok) {
      typeof window !== 'undefined' && verifySession(json.seedSessionToken);
    }
    return { status: response.status, data: json };
  } catch (err) {
    if (err instanceof SessionVerificationError) {
      return {
        status: 401,
        data: {
          msg: 'SessionVerificationError',
          token: err.token,
          sessionToken: err.sessionToken,
        },
      };
    }
    return { status: 500, data: err };
  }
}

// USER
export async function apiUserGet() {
  try {
    const endpoint = `${url}/user`;

    const response = await fetch(endpoint, opt('GET'));
    const json = await response.json();

    return { status: response.status, data: json };
  } catch (err) {
    if (err instanceof SessionVerificationError) {
      return {
        status: 401,
        data: {
          msg: 'SessionVerificationError',
          token: err.token,
          sessionToken: err.sessionToken,
        },
      };
    }
    return { status: 500, data: err };
  }
}

export async function apiUserPut(data) {
  try {
    const endpoint = `${url}/user`;
    const response = await fetch(endpoint, opt('PUT', data));
    const json = await response.json();
    typeof window !== 'undefined' && verifySession(json.seedSessionToken);
    return { status: response.status, data: json };
  } catch (err) {
    if (err instanceof SessionVerificationError) {
      return {
        status: 401,
        data: {
          msg: 'SessionVerificationError',
          token: err.token,
          sessionToken: err.sessionToken,
        },
      };
    }
    return { status: 500, data: err };
  }
}

export async function apiLoginRoutingGet(email: string) {
  try {
    const endpoint = `/account/login-routing/?email=${encodeURIComponent(email)}`;
    const response = await getRequest(endpoint);

    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    console.error('Error in apiLoginRoutingGet:', err);
    return { status: 500, data: err };
  }
}

export async function apiGetChatToken() {
  const response = await postRequest('/account/user/chat/token/', {});
  const json = await response.json();
  return json;
}

export async function apiGetCancellationReasons(
  subscriptionId: string,
): Promise<{
  status: number;
  data: any;
}> {
  try {
    const endpoint = `${url}/subscription/cancellationReasons/${subscriptionId}`;
    const response = await fetch(endpoint);
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: err };
  }
}

export async function apiApplyCoupon(data) {
  const response = await postRequest('/ecommerce/coupon/redemption/', data);
  const json = await response.json();
  return json;
}

// Survey
export async function apiSurveyGet(surveyName: string) {
  const response = await getRequest(`/survey/${surveyName}/`);
  const json = await response.json();
  return json;
}

export async function apiUserSurveyGet(surveyName: string) {
  const response = await getRequest(`/survey/user-survey/${surveyName}/`);
  const json = await response.json();
  return json;
}

export async function apiSurveySubmit(surveyName: string, data: any) {
  const response = await postRequest(
    `/survey/survey-answers/?survey_name=${surveyName}/`,
    data,
  );
  const json = await response.json();
  return json;
}

export async function apiUserSurveySubmit(
  surveyId: number,
  data: any,
): Promise<SurveySubmitResult> {
  const response = await postRequest(
    `/survey/${surveyId}/survey-answers/`,
    data,
  );
  const json = await response.json();
  return json;
}

export async function apiExternalSurveySubmit(
  surveyName: string,
  surveyResponses: Array<{
    survey_name: string;
    question_id: number | null;
    question: string;
    option_id: number | null;
    option_text: string | null;
    answer_text: string;
  }>,
): Promise<{
  success: boolean;
  data?: {
    msg: string;
    survey_name: string;
    user_id: string;
    surveyName: string;

    results: Array<{
      points_awarded: number;
      shareback_data: any;
    }>;
  };
  error?: string;
}> {
  try {
    const payload = {
      survey_name: surveyName,
      survey_responses: surveyResponses,
    };
    // survey/external/survey-answers/

    const response = await postRequest(
      '/survey/external/survey-answers/',
      payload,
    );
    const json = await response.json();

    // Check if the response indicates success
    if (response.ok && json.msg === 'created') {
      return {
        success: true,
        data: json,
      };
    } else {
      return {
        success: false,
        error: json.msg || `HTTP ${response.status}: Request failed`,
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

export async function apiCheckLocation(): Promise<{
  status: number;
  data: { country: string; region: string };
}> {
  try {
    const endpoint = `${url}/auth/location`;
    const response = await fetch(endpoint, opt('GET'));
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: { country: 'UNKNOWN', region: 'UNKNOWN' } };
  }
}

// SHOPIFY AUTH
export async function apiInitiateShopifyAuth() {
  try {
    const endpoint = `${url}/auth/shopify/authorize`;
    // This will redirect, so we need to handle it differently
    window.location.href = endpoint;
  } catch (err) {
    console.error('Error initiating Shopify auth:', err);
  }
}

export async function apiRefreshShopifyToken(refreshToken: string) {
  try {
    const endpoint = `${url}/auth/shopify/refresh`;
    const response = await fetch(
      endpoint,
      opt('POST', { refresh_token: refreshToken }),
    );
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: { error: 'Failed to refresh token' } };
  }
}

// SHOPIFY
/*
 * Base fetch to Shopify middleware for handling error responses
 *
 * @param endpoint: full endpoint to hit
 * @param options: should use the `opt` function (opt('GET') or opt('POST', data))
 *
 * @returns fetch response or error object in case of failure
 */
async function baseShopifyFetch(endpoint: string, options: RequestInit) {
  try {
    const response = await fetch(endpoint, options);
    const json = await response.json();

    if (response.status > 399) {
      return { status: response.status, error: json.error };
    } else {
      return { status: response.status, data: json };
    }
  } catch (error) {
    trackCustomError(
      error instanceof Error ? error : new Error(String(error)),
      { endpoint },
    );
    return { status: 500, error: error };
  }
}

export async function getShopifyVariantInfo(country?: string) {
  const query = country ? `?country=${encodeURIComponent(country)}` : '';
  const endpoint = `${url}/products/allVariantInfo${query}`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function apiGetLocalization() {
  const endpoint = `${url}/products/localization`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function getShopifyFullProductContent({
  productID,
  productHandle,
}: {
  productID?: string;
  productHandle: string;
}) {
  const endpoint = productID
    ? `${url}/products/productInfo?productID=${encodeURIComponent(productID)}`
    : `${url}/products/productInfo?productHandle=${encodeURIComponent(productHandle)}`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function getProductMetaobject(metaobjectID: string) {
  const endpoint = `${url}/products/productMetaobject?metaobjectID=${encodeURIComponent(metaobjectID)}`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function getHomepageContents(handle?: string) {
  const endpoint = handle
    ? `${url}/homepage/contents?handle=${encodeURIComponent(handle)}`
    : `${url}/homepage/contents`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function apiGetShopifyCart({ cartID }) {
  const endpoint = `${url}/cart?cartID=${encodeURIComponent(cartID)}`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function apiGetShopifyDiscountInfo({ discountCode }) {
  const endpoint = `${url}/cart/discountCode?discountCode=${encodeURIComponent(discountCode)}`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function apiUpdateShopifyCart({ cartID, lines }) {
  const data = { cartID, lines };
  const endpoint = `${url}/cart/updateCart`;
  return await baseShopifyFetch(endpoint, opt('POST', data));
}

export async function apiUpdateShopifyCartAttributes(
  { cartID, attributes }: { cartID: string; attributes?: any[] },
  fetchOptions?: { keepalive?: boolean },
) {
  const data = { cartID, attributes };
  const endpoint = `${url}/cart/updateCartAttributes`;
  const options = opt('POST', data);
  if (fetchOptions?.keepalive) {
    options.keepalive = true;
  }
  return await baseShopifyFetch(endpoint, options);
}

export async function apiCreateShopifyCart({
  attributes,
  lines,
  discountCodes = false,
  buyerEmail = undefined,
  countryCode = undefined,
}: {
  attributes?: any;
  lines: any[];
  discountCodes?: any;
  buyerEmail?: string;
  countryCode?: string;
}) {
  const data = { attributes, lines, discountCodes, buyerEmail, countryCode };
  const endpoint = `${url}/cart/createCart`;
  return await baseShopifyFetch(endpoint, opt('POST', data));
}

export async function apiAddShopifyDiscount({ cartID, discountCode }) {
  const data = { cartID, discountCode };
  const endpoint = `${url}/cart/addCartDiscount`;
  return await baseShopifyFetch(endpoint, opt('POST', data));
}

export async function apiUpdateCartBuyerIdentity({
  cartID,
  customerAccessToken,
  email,
  countryCode,
}: {
  cartID: string;
  customerAccessToken?: string;
  email?: string;
  countryCode?: string;
}) {
  const data = { cartID, customerAccessToken, email, countryCode };
  const endpoint = `${url}/cart/updateBuyerIdentity`;
  return await baseShopifyFetch(endpoint, opt('POST', data));
}

export async function apiGetUserRechargePortalLink({
  userEmail,
  userToken,
}: {
  userEmail: string;
  userToken?: string;
}) {
  let endpoint = `${url}/recharge/getUserAffinityPortalURL?email=${encodeURIComponent(userEmail)}`;
  if (userToken) {
    endpoint += `&userToken=${encodeURIComponent(userToken)}`;
  }
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function apiGetChurnLandingPageURL(
  subscriptionId: number | string,
) {
  const endpoint = `${url}/recharge/getChurnLandingPageURL?subscriptionId=${encodeURIComponent(subscriptionId)}`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function apiGetUserPaymentInfo({ userEmail, userToken }) {
  const endpoint = `${url}/recharge/getUserPayments?email=${encodeURIComponent(userEmail)}&userToken=${encodeURIComponent(userToken)}`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function apiGetUserLoyaltyCouponRedemptions({ userEmail }) {
  const endpoint = `${url}/loyalty/getUserCoupons?userEmail=${encodeURIComponent(userEmail)}`;
  return await baseShopifyFetch(endpoint, opt('GET'));
}

export async function apiCreateUserLoyaltyCouponRedemption({
  userEmail,
  variantID,
  sellingPlan,
  couponCode,
  shippingCode,
}) {
  const data = { userEmail, variantID, sellingPlan, couponCode, shippingCode };
  const endpoint = `${url}/loyalty/createUserLoyaltyCoupon`;
  return await baseShopifyFetch(endpoint, opt('POST', data));
}

export async function apiShopifyInitiateAuth(
  returnTo?: string,
  options: { silent?: boolean } = {},
) {
  try {
    const params = new URLSearchParams();
    if (returnTo) {
      params.set('return_to', returnTo);
    }
    if (options.silent) {
      params.set('silent', '1');
    }
    const query = params.toString();
    const endpoint = `${url}/auth/shopify/authorize${query ? `?${query}` : ''}`;
    const response = await fetch(endpoint, opt('GET'));
    const json = await response.json();
    return { status: response.status, data: json };
  } catch (err) {
    return { status: 500, data: { error: 'Failed to initiate Shopify auth' } };
  }
}
